/**
 * Читает штрихкод целиком на устройстве. Фото не загружается ни на наш сервер,
 * ни в сторонний сервис — наружу уйдут только распознанные цифры.
 */
export async function barcodeFromImage(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const reader = await createReader();
    const image = await loadImage(url);

    // Галерея iOS обычно отдаёт уменьшенную копию, а capture="environment" —
    // полноразмерный кадр 12–48 Мп. ZXing на таком исходнике часто не находит
    // полосы, хотя тот же снимок после сохранения в «Фото» читается. Маленький
    // кадр сначала пробуем как есть, большой сразу приводим к рабочему размеру.
    if (Math.max(image.naturalWidth, image.naturalHeight) <= 2200) {
      const direct = tryDecode(() => reader.decode(image));
      if (direct) return direct;
    }

    // Несколько дешёвых проходов закрывают типичные проблемы мобильной камеры:
    // слишком большое фото, EXIF-поворот и слабый контраст на глянцевой упаковке.
    const variants: Array<[
      maxSide: number,
      turns: number,
      contrast: boolean,
      cropTop?: number,
      cropHeight?: number,
    ]> = [
      // Размер, который iOS обычно отдаёт при выборе из «Фото» и на котором
      // эти же кадры стабильно распознаются.
      [1024, 0, false],
      [1800, 0, false],
      [1400, 0, false],
      // ZXing ищет полосы вдоль нескольких строк около центра. Если штрихкод
      // находится сверху или снизу полноразмерного фото, помещаем по очереди
      // каждую область в центр отдельного кадра.
      [1800, 0, false, 0, 0.55],
      [1800, 0, false, 0.225, 0.55],
      [1800, 0, false, 0.45, 0.55],
      [1600, 1, false],
      [1600, 3, false],
      [1400, 0, true],
      [1600, 0, true, 0, 0.55],
      [1600, 0, true, 0.45, 0.55],
      [1400, 1, true],
    ];

    for (const [maxSide, turns, contrast, cropTop, cropHeight] of variants) {
      const canvas = renderVariant(
        image,
        maxSide,
        turns,
        contrast,
        cropTop,
        cropHeight,
      );
      const decoded = tryDecode(() => reader.decodeFromCanvas(canvas));
      // Освобождаем пиксельный буфер сразу: в Telegram WebView памяти меньше,
      // чем в обычном Safari, особенно после полноразмерного фото камеры.
      canvas.width = 1;
      canvas.height = 1;
      if (decoded) return decoded;
    }

    throw new Error('barcode-not-found');
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Сканирует поток задней камеры до первого корректного товарного штрихкода.
 * Это надёжнее системной кнопки «сделать фото»: камера успевает сфокусироваться,
 * а ZXing видит несколько кадров вместо одного полноразмерного HEIC/JPEG.
 */
export async function barcodeFromCamera(
  video: HTMLVideoElement,
  onDetected: (barcode: string) => void,
): Promise<() => void> {
  const reader = await createReader();
  let delivered = false;
  const controls = await reader.decodeFromConstraints(
    {
      audio: false,
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    },
    video,
    (result, _error, scannerControls) => {
      if (delivered || !result) return;
      const digits = normalizedBarcode(result.getText());
      if (!digits) return;
      delivered = true;
      scannerControls.stop();
      onDetected(digits);
    },
  );
  return () => controls.stop();
}

/** ZXing подгружается лениво — только при открытии сканера. */
async function createReader() {
  const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] =
    await Promise.all([import('@zxing/browser'), import('@zxing/library')]);
  const hints = new Map();
  hints.set(DecodeHintType.TRY_HARDER, true);
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.ITF,
  ]);
  return new BrowserMultiFormatReader(hints, {
    delayBetweenScanAttempts: 120,
    delayBetweenScanSuccess: 500,
  });
}

function tryDecode(decode: () => { getText(): string }): string | null {
  try {
    return normalizedBarcode(decode().getText());
  } catch {
    return null;
  }
}

function normalizedBarcode(value: string): string | null {
  const digits = value.replace(/\D/g, '');
  return /^\d{6,14}$/.test(digits) ? digits : null;
}

async function loadImage(url: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  if (image.decode) {
    await image.decode();
    return image;
  }
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('image-not-readable'));
  });
  return image;
}

/** Рисует уменьшенный, при необходимости повёрнутый и контрастный вариант. */
function renderVariant(
  image: HTMLImageElement,
  maxSide: number,
  quarterTurns: number,
  highContrast: boolean,
  cropTop = 0,
  cropHeight = 1,
): HTMLCanvasElement {
  const sourceWidth = image.naturalWidth;
  const sourceY = Math.round(image.naturalHeight * cropTop);
  const sourceHeight = Math.min(
    image.naturalHeight - sourceY,
    Math.round(image.naturalHeight * cropHeight),
  );
  const scale = Math.min(1, maxSide / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const sideways = quarterTurns % 2 === 1;
  const canvas = document.createElement('canvas');
  canvas.width = sideways ? height : width;
  canvas.height = sideways ? width : height;

  const context = canvas.getContext('2d', { willReadFrequently: highContrast });
  if (!context) throw new Error('canvas-unavailable');
  context.fillStyle = '#fff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate((quarterTurns * Math.PI) / 2);
  context.drawImage(
    image,
    0,
    sourceY,
    sourceWidth,
    sourceHeight,
    -width / 2,
    -height / 2,
    width,
    height,
  );

  if (highContrast) enhanceContrast(context, canvas.width, canvas.height);
  return canvas;
}

/** Чёрно-белая упаковка после блика остаётся читаемой для бинаризатора ZXing. */
function enhanceContrast(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
) {
  const frame = context.getImageData(0, 0, width, height);
  const pixels = frame.data;
  for (let i = 0; i < pixels.length; i += 4) {
    const light = pixels[i]! * 0.299 + pixels[i + 1]! * 0.587 + pixels[i + 2]! * 0.114;
    const value = Math.max(0, Math.min(255, (light - 128) * 1.65 + 128));
    pixels[i] = value;
    pixels[i + 1] = value;
    pixels[i + 2] = value;
  }
  context.putImageData(frame, 0, 0);
}
